<script setup lang="ts">
import { computed } from "vue";
import type { ReadinessIssue, ReadinessDimension } from "@/types/readiness";

const props = defineProps<{
  open: boolean;
  issues: ReadinessIssue[];
  advisories: ReadinessIssue[];
  submitting?: boolean;
  /** Enables the "Open Connections" shortcut on connection issues. */
  projectId?: string;
}>();

const emit = defineEmits<{
  "run-anyway": [];
  cancel: [];
}>();

const dimensionLabel: Record<ReadinessDimension, string> = {
  Wizard: "Setup",
  Connection: "Connection",
  Datastore: "Datastore",
};

const issueCount = computed(() => props.issues.length);
</script>

<template>
  <div
    class="modal fade"
    :class="{ show: open }"
    :style="{ display: open ? 'block' : 'none' }"
    @click.self="emit('cancel')"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-warning-emphasis">
            <i class="bi bi-exclamation-triangle me-2"></i>This run isn't ready
          </h2>
          <button type="button" class="btn-close" @click="emit('cancel')"></button>
        </div>

        <div class="modal-body">
          <p>
            This workflow has {{ issueCount }} configuration
            {{ issueCount === 1 ? "issue" : "issues" }} to resolve. Run anyway and any unresolved
            values are sent as <code>undefined</code>.
          </p>

          <ul class="list-group mb-0">
            <li v-for="issue in issues" :key="`${issue.dimension}:${issue.reference}`" class="list-group-item">
              <span class="badge bg-warning text-dark me-2">{{ dimensionLabel[issue.dimension] }}</span>
              {{ issue.message }}
              <router-link
                v-if="issue.dimension === 'Connection' && projectId"
                :to="{ name: 'project-connections', params: { projectid: projectId } }"
                class="ms-1"
              >
                Open Connections
              </router-link>
            </li>
          </ul>

          <template v-if="advisories.length">
            <p class="text-muted small mt-3 mb-1">Also noted:</p>
            <ul class="list-group list-group-flush mb-0">
              <li v-for="advisory in advisories" :key="`${advisory.dimension}:${advisory.reference}`"
                  class="list-group-item text-muted small px-0">
                <i class="bi bi-info-circle me-2"></i>{{ advisory.message }}
              </li>
            </ul>
          </template>
        </div>

        <div class="modal-footer">
          <button class="bb-btn-cancel" :disabled="submitting" @click="emit('cancel')">Cancel</button>
          <button class="btn btn-warning text-dark" :disabled="submitting" @click="emit('run-anyway')">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Run anyway
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="open" class="modal-backdrop fade show" @click="emit('cancel')"></div>
</template>
